/**
 * Display data (ADR-0117): the pure mapping from a view block (`views.ts`, the shared contract
 * with the platform side) to what each display draws — rows and columns of a table, the fields of
 * a record, points and lines of a map, the people and links of a network, the events of a
 * timeline, the series of a chart and a document's address. Defensive: a tool's result is data
 * from a system, never trusted to have the shape its hints promise.
 */
import { inferView, isViewKind, VIEW_KINDS, type ResultRef, type ViewBlock, type ViewHints, type ViewKind } from "./views";

export type Row = Record<string, unknown>;
export type CellType = "text" | "number" | "date" | "datetime" | "status" | "entity" | "money" | "link";
export type Tone = "success" | "warning" | "error" | "info" | "default";


const isObj = (v: unknown): v is Row => !!v && typeof v === "object" && !Array.isArray(v);
const isScalar = (v: unknown) => v === null || ["string", "number", "boolean"].includes(typeof v);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/** `a.b.0.c` inside an object (arrays by index). */
export function pick(obj: unknown, path: string | undefined): unknown {
  if (!path) return obj;
  return path.split(".").reduce<unknown>((n, k) => (Array.isArray(n) ? n[Number(k)] : isObj(n) ? n[k] : undefined), obj);
}

/** A masked value as the platform writes it (`[PERSON_1]`, `***`) or a `{masked: true}` cell. */
export function isMasked(v: unknown): boolean {
  if (typeof v === "string") return /^\[[A-Z][A-Z_]*_\d+\]$/.test(v.trim()) || /^\*{3,}$/.test(v.trim());
  return isObj(v) && v.masked === true;
}

/** The rows of a result: `hints.rows`, the result itself, or its first list of objects. */
export function rowsOf(data: unknown, hints?: ViewHints): Row[] {
  const at = hints?.rows ? pick(data, hints.rows) : data;
  if (Array.isArray(at)) return at.map((r) => (isObj(r) ? r : { value: r }));
  if (isObj(at)) {
    for (const k of ["items", "rows", "results", "records", "data"]) if (Array.isArray(at[k])) return rowsOf(at[k]);
    const list = Object.values(at).find((v) => Array.isArray(v) && v.length && v.every(isObj));
    if (list) return list as Row[];
    return [at];
  }
  return at === undefined || at === null ? [] : [{ value: at }];
}

/** The one record a record card or key-value view shows. */
export function recordOf(data: unknown, hints?: ViewHints): Row {
  // A result with fields of its own is the record (a case file whose `rows` are its timeline);
  // otherwise the record is found where the rows are (`{items: [one]}`).
  if (isObj(data) && Object.values(data).some((v) => isScalar(v) || isMasked(v) || isText(v))) return data;
  const at = hints?.rows ? pick(data, hints.rows) : data;
  if (isObj(at)) return at;
  return rowsOf(at)[0] ?? {};
}

/** `{en, ar}`: a text the system gives in both languages. */
const isText = (v: unknown): v is { en?: unknown; ar?: unknown } =>
  isObj(v) && Object.keys(v).length > 0 && Object.keys(v).every((k) => k === "en" || k === "ar") && Object.values(v).every((x) => typeof x === "string");

/** A record's parts drawn under its fields: the hinted timeline, places and links, and every
 * other list of objects in it as a small table (the people in a case file, say). */
export interface RecordSection {
  kind: "timeline" | "map" | "network" | "table";
  field: string;
  hints: ViewHints;
}

export function recordSections(record: Row, hints?: ViewHints): RecordSection[] {
  const out: RecordSection[] = [];
  const used = new Set<string>();
  const top = (path: string | undefined) => (path ? path.split(".")[0]! : "");
  if (hints?.time && hints.rows && Array.isArray(pick(record, hints.rows))) {
    out.push({ kind: "timeline", field: hints.rows, hints: { rows: hints.rows, time: hints.time } });
    used.add(top(hints.rows));
  }
  if (hints?.geo && "geojson" in hints.geo && pick(record, hints.geo.geojson) !== undefined) {
    out.push({ kind: "map", field: hints.geo.geojson, hints: { geo: hints.geo } });
    used.add(top(hints.geo.geojson));
  }
  if (hints?.network && Array.isArray(pick(record, hints.network.nodes))) {
    out.push({ kind: "network", field: hints.network.nodes, hints: { network: hints.network } });
    used.add(top(hints.network.nodes)).add(top(hints.network.edges));
  }
  for (const [k, v] of Object.entries(record)) {
    if (used.has(k) || !Array.isArray(v) || !v.length || !v.every(isObj)) continue;
    // Labels of a part's own columns are given as `<part>.<field>` (e.g. `parties.role`).
    const columns = (hints?.columns ?? []).filter((c) => c.field.startsWith(`${k}.`)).map((c) => ({ ...c, field: c.field.slice(k.length + 1) }));
    out.push({ kind: "table", field: k, hints: { rows: k, entityFields: hints?.entityFields, status: hints?.status, valueLabels: hints?.valueLabels, ...(columns.length ? { columns } : {}) } });
  }
  return out;
}

function inferType(field: string, values: unknown[], hints?: ViewHints): CellType {
  if (hints?.status?.field === field) return "status";
  if (hints?.entityFields?.[field] || hints?.entity?.key === field) return "entity";
  const present = values.filter((v) => v !== null && v !== undefined && v !== "");
  if (!present.length) return "text";
  if (present.every((v) => typeof v === "number")) return /amount|price|total|balance|fee|cost/i.test(field) ? "money" : "number";
  if (present.every((v) => typeof v === "string" && ISO_DATE.test(v))) return "date";
  if (present.every((v) => typeof v === "string" && ISO_DATETIME.test(v))) return "datetime";
  if (present.every((v) => typeof v === "string" && v.startsWith("/") && !v.startsWith("//"))) return "link";
  return "text";
}

export interface Column {
  field: string;
  label: { en: string; ar: string } | null;
  type: CellType;
}

/** A code in words in the person's language: the display's `valueLabels`, else the code in words. */
export function valueLabel(field: string, value: string, hints: ViewHints | undefined, lang: "ar" | "en"): string {
  const l = hints?.valueLabels?.[field]?.[value];
  return l?.[lang] || l?.en || fieldWords(value);
}

/** A field's label in the person's language: the display's column label, else its name in words. */
export function labelOf(field: string, hints: ViewHints | undefined, lang: "ar" | "en"): string {
  const c = hints?.columns?.find((x) => x.field === field);
  return c?.label?.[lang] || c?.label?.en || fieldWords(field);
}

/** A field name as words (`court_order_no` → "Court order no"), for columns without a label. */
export function fieldWords(field: string): string {
  const s = field.replace(/[_.-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : field;
}

/** The hinted columns, else every plain field of the first rows (at most 8), types guessed. */
export function columnsOf(rows: Row[], hints?: ViewHints): Column[] {
  const sample = rows.slice(0, 25);
  if (hints?.columns?.length) {
    return hints.columns.map((c) => ({ field: c.field, label: c.label ?? null, type: c.type ?? inferType(c.field, sample.map((r) => pick(r, c.field)), hints) }));
  }
  const fields: string[] = [];
  for (const r of sample) for (const [k, v] of Object.entries(r)) if (!fields.includes(k) && (isScalar(v) || isMasked(v))) fields.push(k);
  return fields.slice(0, 8).map((f) => ({ field: f, label: null, type: inferType(f, sample.map((r) => r[f]), hints) }));
}

export function toneOf(hints: ViewHints | undefined, row: Row): Tone | null {
  if (!hints?.status) return null;
  const v = pick(row, hints.status.field);
  return typeof v === "string" || typeof v === "number" ? (hints.status.tones[String(v)] ?? "default") : null;
}

export interface EntityRef {
  type: string;
  key: string;
}

/** The record a cell points to (a person, a vehicle, an order), when the hints say so. */
export function entityOf(hints: ViewHints | undefined, row: Row, field: string): EntityRef | null {
  const v = pick(row, field);
  if ((typeof v !== "string" && typeof v !== "number") || isMasked(v) || v === "") return null;
  const t = hints?.entityFields?.[field]?.type ?? (hints?.entity?.key === field ? hints.entity.type : null);
  return t ? { type: t, key: String(v) } : null;
}

/** Every entity a view mentions (for record cards and suggested actions), without repeats. */
export function entitiesOf(block: ViewBlock, data: unknown): EntityRef[] {
  const hints = block.hints;
  if (!hints?.entity && !hints?.entityFields) return [];
  const rows = block.kind === "record" || block.kind === "kv" ? [recordOf(data, hints)] : rowsOf(data, hints);
  const fields = [...new Set([...(hints.entity ? [hints.entity.key] : []), ...Object.keys(hints.entityFields ?? {})])];
  const seen = new Set<string>();
  const out: EntityRef[] = [];
  for (const r of rows.slice(0, 200)) {
    for (const f of fields) {
      const e = entityOf(hints, r, f);
      if (e && !seen.has(`${e.type}:${e.key}`)) {
        seen.add(`${e.type}:${e.key}`);
        out.push(e);
      }
    }
  }
  return out;
}

/** Sorted copy: numbers by value, dates by time, text in the reader's language. */
export function sortRows(rows: Row[], field: string, dir: "asc" | "desc", lang: "ar" | "en" = "en"): Row[] {
  const coll = new Intl.Collator(lang, { numeric: true, sensitivity: "base" });
  const val = (r: Row) => pick(r, field);
  const out = [...rows].sort((a, b) => {
    const x = val(a);
    const y = val(b);
    if (x === y) return 0;
    if (x === null || x === undefined || x === "") return 1;
    if (y === null || y === undefined || y === "") return -1;
    if (typeof x === "number" && typeof y === "number") return x - y;
    return coll.compare(String(x), String(y));
  });
  return dir === "desc" ? out.reverse() : out;
}

// ---------------------------------------------------------------------------------- record

export interface Field {
  key: string;
  value: unknown;
  type: CellType;
}

/** A record's plain fields in order (nested objects flattened one level: `address.city`). */
export function fieldsOf(record: Row, hints?: ViewHints, lang: "ar" | "en" = "en"): Field[] {
  const out: Field[] = [];
  const push = (k: string, v: unknown) => out.push({ key: k, value: v, type: inferType(k, [v], hints) });
  for (const [k, v] of Object.entries(record)) {
    // `meta` / `_meta`: how the system describes its answer (source, synthetic), not the record.
    if (k === "meta" || k === "_meta") continue;
    if (isScalar(v) || isMasked(v)) push(k, v);
    else if (isText(v)) push(k, v[lang] || v.en || v.ar);
    else if (isObj(v)) {
      for (const [k2, v2] of Object.entries(v)) if (isScalar(v2) || isMasked(v2)) push(`${k}.${k2}`, v2);
    } else if (Array.isArray(v) && v.every(isScalar)) push(k, v.join(", "));
  }
  return out;
}

/** A title for a record or list item: the hinted label field, else a name-like field. */
export function titleOf(row: Row, preferred?: string, lang: "ar" | "en" = "en"): string {
  const keys = [preferred, "name", "full_name", "title", "label", lang === "ar" ? "name_ar" : "name_en", "subject", "id"].filter((k): k is string => !!k);
  for (const k of keys) {
    const raw = pick(row, k);
    const v = isText(raw) ? raw[lang] || raw.en : raw;
    if ((typeof v === "string" && v.trim()) || typeof v === "number") return isMasked(v) ? "" : String(v);
  }
  const first = Object.values(row).find((v) => typeof v === "string" && v.trim() && !isMasked(v));
  return typeof first === "string" ? first : "";
}

// -------------------------------------------------------------------------------- timeline

export interface TimeItem {
  at: string;
  end: string | null;
  label: string;
  row: Row;
}

const timeFieldOf = (rows: Row[]): string | undefined => {
  const first = rows[0] ?? {};
  return Object.keys(first).find((k) => typeof first[k] === "string" && (ISO_DATETIME.test(first[k] as string) || ISO_DATE.test(first[k] as string)));
};

export function timelineOf(rows: Row[], hints?: ViewHints): TimeItem[] {
  const field = hints?.time?.field ?? timeFieldOf(rows);
  if (!field) return [];
  const items: TimeItem[] = [];
  for (const r of rows) {
    const at = pick(r, field);
    if (typeof at !== "string" || Number.isNaN(Date.parse(at))) continue;
    const end = hints?.time?.end ? pick(r, hints.time.end) : null;
    items.push({ at, end: typeof end === "string" ? end : null, label: hints?.time?.label ? String(pick(r, hints.time.label) ?? "") : titleOf(Object.fromEntries(Object.entries(r).filter(([k]) => k !== field))), row: r });
  }
  return items.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

// ------------------------------------------------------------------------------------- map

export type LngLat = [number, number];
export interface MapPoint {
  at: LngLat;
  label: string;
  row: Row;
}
export interface MapShape {
  kind: "line" | "area";
  path: LngLat[];
  label: string;
}
export interface MapData {
  points: MapPoint[];
  shapes: MapShape[];
}

const num = (v: unknown): number | null => {
  const n = typeof v === "string" && v.trim() ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};
const lngLat = (c: unknown): LngLat | null => (Array.isArray(c) && num(c[0]) !== null && num(c[1]) !== null && Math.abs(c[1] as number) <= 90 && Math.abs(c[0] as number) <= 180 ? [c[0] as number, c[1] as number] : null);

function fromGeometry(g: unknown, label: string, row: Row, out: MapData): void {
  if (!isObj(g)) return;
  const coords = g.coordinates;
  const path = (cs: unknown): LngLat[] => (Array.isArray(cs) ? cs.map(lngLat).filter((p): p is LngLat => !!p) : []);
  switch (g.type) {
    case "Point": {
      const p = lngLat(coords);
      if (p) out.points.push({ at: p, label, row });
      break;
    }
    case "MultiPoint":
      for (const p of path(coords)) out.points.push({ at: p, label, row });
      break;
    case "LineString":
      out.shapes.push({ kind: "line", path: path(coords), label });
      break;
    case "MultiLineString":
      for (const l of Array.isArray(coords) ? coords : []) out.shapes.push({ kind: "line", path: path(l), label });
      break;
    case "Polygon":
      if (Array.isArray(coords) && coords[0]) out.shapes.push({ kind: "area", path: path(coords[0]), label });
      break;
    case "MultiPolygon":
      for (const poly of Array.isArray(coords) ? coords : []) if (Array.isArray(poly) && poly[0]) out.shapes.push({ kind: "area", path: path(poly[0]), label });
      break;
    case "GeometryCollection":
      for (const x of Array.isArray(g.geometries) ? g.geometries : []) fromGeometry(x, label, row, out);
      break;
  }
}

function fromGeoJson(v: unknown, out: MapData): void {
  if (!isObj(v)) return;
  if (v.type === "FeatureCollection" && Array.isArray(v.features)) for (const f of v.features) fromGeoJson(f, out);
  else if (v.type === "Feature") {
    const props = isObj(v.properties) ? v.properties : {};
    fromGeometry(v.geometry, titleOf(props), props, out);
  } else fromGeometry(v, "", {}, out);
}

const LAT = ["lat", "latitude"];
const LNG = ["lng", "lon", "long", "longitude"];

/** Points and lines to draw: GeoJSON (hinted or found) or rows with latitude and longitude. */
export function geoOf(data: unknown, hints?: ViewHints): MapData {
  const out: MapData = { points: [], shapes: [] };
  const geo = hints?.geo;
  if (geo && "geojson" in geo) {
    fromGeoJson(pick(data, geo.geojson), out);
    return out;
  }
  if (isObj(data) && (data.type === "FeatureCollection" || data.type === "Feature")) {
    fromGeoJson(data, out);
    return out;
  }
  const rows = rowsOf(data, hints);
  const first = rows[0] ?? {};
  const latK = geo && "lat" in geo ? geo.lat : LAT.find((k) => k in first);
  const lngK = geo && "lng" in geo ? geo.lng : LNG.find((k) => k in first);
  if (!latK || !lngK) return out;
  for (const r of rows) {
    const lat = num(pick(r, latK));
    const lng = num(pick(r, lngK));
    if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
    out.points.push({ at: [lng, lat], label: titleOf(r), row: r });
  }
  return out;
}

/** The box around everything to draw, padded; null when there is nothing. */
export function boundsOf(m: MapData): { minLng: number; minLat: number; maxLng: number; maxLat: number } | null {
  const all = [...m.points.map((p) => p.at), ...m.shapes.flatMap((s) => s.path)];
  if (!all.length) return null;
  let [minLng, minLat, maxLng, maxLat] = [180, 90, -180, -90];
  for (const [x, y] of all) {
    minLng = Math.min(minLng, x);
    maxLng = Math.max(maxLng, x);
    minLat = Math.min(minLat, y);
    maxLat = Math.max(maxLat, y);
  }
  const pad = Math.max(0.05, (maxLng - minLng) * 0.15, (maxLat - minLat) * 0.15);
  return { minLng: Math.max(-180, minLng - pad), minLat: Math.max(-85, minLat - pad), maxLng: Math.min(180, maxLng + pad), maxLat: Math.min(85, maxLat + pad) };
}

// --------------------------------------------------------------------------------- network

export interface NetNode {
  id: string;
  label: string;
  type: string;
  row: Row;
}
export interface NetEdge {
  source: string;
  target: string;
  label: string;
}

export function networkOf(data: unknown, hints?: ViewHints): { nodes: NetNode[]; edges: NetEdge[] } {
  const n = hints?.network;
  const rawNodes = pick(data, n?.nodes ?? "nodes");
  const rawEdges = pick(data, n?.edges ?? (isObj(data) && Array.isArray(data.links) ? "links" : "edges"));
  const nodes: NetNode[] = [];
  const ids = new Set<string>();
  for (const r of Array.isArray(rawNodes) ? rawNodes : []) {
    if (!isObj(r)) continue;
    const id = pick(r, n?.nodeId ?? "id");
    if ((typeof id !== "string" && typeof id !== "number") || ids.has(String(id))) continue;
    ids.add(String(id));
    const label = pick(r, n?.nodeLabel ?? "label");
    const type = pick(r, n?.nodeType ?? "type");
    nodes.push({ id: String(id), label: typeof label === "string" && !isMasked(label) ? label : titleOf(r) || String(id), type: typeof type === "string" ? type : "other", row: r });
  }
  const edges: NetEdge[] = [];
  for (const r of Array.isArray(rawEdges) ? rawEdges : []) {
    if (!isObj(r)) continue;
    const s = String(pick(r, n?.source ?? "source") ?? "");
    const t = String(pick(r, n?.target ?? "target") ?? "");
    if (!ids.has(s) || !ids.has(t) || s === t) continue;
    const label = pick(r, n?.edgeLabel ?? "label");
    edges.push({ source: s, target: t, label: typeof label === "string" ? label : "" });
  }
  return { nodes: nodes.slice(0, 150), edges: edges.slice(0, 400) };
}

/**
 * A small deterministic force layout (no randomness, so a network looks the same each time):
 * nodes start on a circle, repel each other, links pull, everything is kept inside the box.
 */
export function forceLayout(nodes: { id: string }[], edges: { source: string; target: string }[], w: number, h: number, iterations = 220): Record<string, { x: number; y: number }> {
  const n = nodes.length;
  const pos = nodes.map((_, i) => ({ x: w / 2 + Math.cos((2 * Math.PI * i) / Math.max(1, n)) * w * 0.3, y: h / 2 + Math.sin((2 * Math.PI * i) / Math.max(1, n)) * h * 0.3 }));
  const index = new Map(nodes.map((x, i) => [x.id, i]));
  const k = Math.sqrt((w * h) / Math.max(1, n)) * 0.6;
  for (let it = 0; it < iterations; it++) {
    const temp = (1 - it / iterations) * Math.min(w, h) * 0.08;
    const disp = pos.map(() => ({ x: 0, y: 0 }));
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = pos[i] as { x: number; y: number };
        const b = pos[j] as { x: number; y: number };
        const dx = a.x - b.x || 0.01;
        const dy = a.y - b.y || 0.01;
        const d = Math.max(1, Math.hypot(dx, dy));
        const f = (k * k) / d;
        (disp[i] as { x: number }).x += (dx / d) * f;
        (disp[i] as { y: number }).y += (dy / d) * f;
        (disp[j] as { x: number }).x -= (dx / d) * f;
        (disp[j] as { y: number }).y -= (dy / d) * f;
      }
    }
    for (const e of edges) {
      const i = index.get(e.source);
      const j = index.get(e.target);
      if (i === undefined || j === undefined) continue;
      const a = pos[i] as { x: number; y: number };
      const b = pos[j] as { x: number; y: number };
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const d = Math.max(1, Math.hypot(dx, dy));
      const f = (d * d) / k;
      (disp[i] as { x: number }).x -= (dx / d) * f;
      (disp[i] as { y: number }).y -= (dy / d) * f;
      (disp[j] as { x: number }).x += (dx / d) * f;
      (disp[j] as { y: number }).y += (dy / d) * f;
    }
    for (let i = 0; i < n; i++) {
      const p = pos[i] as { x: number; y: number };
      const d = disp[i] as { x: number; y: number };
      // A gentle pull to the centre keeps separate groups on screen.
      d.x += (w / 2 - p.x) * 0.02;
      d.y += (h / 2 - p.y) * 0.02;
      const len = Math.max(0.01, Math.hypot(d.x, d.y));
      p.x = Math.min(w - 40, Math.max(40, p.x + (d.x / len) * Math.min(len, temp)));
      p.y = Math.min(h - 24, Math.max(24, p.y + (d.y / len) * Math.min(len, temp)));
    }
  }
  return Object.fromEntries(nodes.map((x, i) => [x.id, pos[i] as { x: number; y: number }]));
}

// ----------------------------------------------------------------------------------- chart

export interface ChartData {
  x: string;
  ys: string[];
  rows: Row[];
  line: boolean;
}

/** The category (or time) field and up to three number fields of the rows. */
export function chartOf(rows: Row[], hints?: ViewHints): ChartData | null {
  const cols = columnsOf(rows, hints);
  const ys = cols.filter((c) => c.type === "number" || c.type === "money").map((c) => c.field).slice(0, 3);
  const x = cols.find((c) => !ys.includes(c.field))?.field;
  if (!x || !ys.length) return null;
  const xType = cols.find((c) => c.field === x)?.type;
  return { x, ys, rows: rows.slice(0, 60), line: xType === "date" || xType === "datetime" };
}

// -------------------------------------------------------------------------------- document

export interface DocumentData {
  url: string | null;
  mime: string;
  text: string | null;
  /** A file carried in the result itself (`content_base64`, a `data:` address), drawn from a local copy. */
  base64: string | null;
}

/** Only this console's own addresses are opened (never another site): `/files/…`, not `//x`. */
export function sameOriginUrl(u: unknown): string | null {
  return typeof u === "string" && /^\/[A-Za-z0-9/_.~%?=&:-]*$/.test(u) && !u.startsWith("//") ? u : null;
}

function mimeOf(url: string | null, given: unknown): string {
  if (typeof given === "string" && given) return given;
  const ext = url?.split("?")[0]?.split(".").pop()?.toLowerCase() ?? "";
  return ({ pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", txt: "text/plain", md: "text/markdown", csv: "text/csv" } as Record<string, string>)[ext] ?? "application/octet-stream";
}

export function documentOf(data: unknown, hints?: ViewHints): DocumentData {
  const d = hints?.document;
  const row = recordOf(data, hints);
  const url = sameOriginUrl(d?.url ?? (d?.field ? pick(row, d.field) : (row.url ?? row.download_path ?? row.link)));
  const mime = mimeOf(url, d?.mime ?? row.media_type ?? row.mime ?? row.mime_type ?? row.content_type);
  const field = d?.field ? pick(row, d.field) : (row.content_base64 ?? undefined);
  const dataUri = typeof data === "string" ? /^data:([a-z/+.-]+);base64,(.*)$/i.exec(data) : null;
  if (dataUri) return { url: null, mime: dataUri[1] as string, text: null, base64: dataUri[2] as string };
  if (!url && typeof field === "string" && !mime.startsWith("text/") && /^[A-Za-z0-9+/=\s]+$/.test(field)) return { url: null, mime, text: null, base64: field.replace(/\s+/g, "") };
  const text = typeof data === "string" ? data : typeof field === "string" ? field : typeof row.text === "string" ? row.text : typeof row.content === "string" ? row.content : null;
  return { url, mime, text: url ? null : text, base64: null };
}

// ----------------------------------------------------------------------- shape and events

/** A display for a result that names none: the platform's own shape rules (`inferView`, ADR-0115). */
export function shapeBlock(data: unknown, ref?: ResultRef): ViewBlock {
  const v = inferView(data);
  return { type: "view", kind: v.kind, ...(v.hints ? { hints: v.hints } : {}), data, ...(ref ? { ref } : {}) };
}

/**
 * A view block from an answer's `answer_views`, a tool's CUSTOM `view` event (kind, hints and a
 * reference — never the data) or a block itself; unknown kinds are dropped.
 */
export function viewBlockOf(v: unknown): ViewBlock | null {
  const inner = isObj(v) && isObj(v.block) ? v.block : isObj(v) && isObj(v.value) && ((v.value as Row).type === "view" || (v.value as Row).name === "view") ? v.value : v;
  if (!isObj(inner) || (inner.type !== "view" && inner.name !== "view") || !isViewKind(inner.kind) || !(VIEW_KINDS as readonly string[]).includes(String(inner.kind))) return null;
  const block: ViewBlock = { type: "view", kind: inner.kind as ViewKind };
  if (isObj(inner.hints)) block.hints = inner.hints as ViewHints;
  const ref = inner.ref;
  if (isObj(ref) && typeof ref.run_id === "string" && typeof ref.call_id === "string") block.ref = { run_id: ref.run_id, call_id: ref.call_id, tool: String(ref.tool ?? ""), action: String(ref.action ?? "") } satisfies ResultRef;
  if ("data" in inner && inner.data !== undefined) block.data = inner.data;
  if (inner.masked === true) block.masked = true;
  return block;
}

/** How many items a view holds (work list counts, "show it bigger" for long results). */
export function viewSize(block: ViewBlock, data: unknown): number {
  if (block.kind === "network") return networkOf(data, block.hints).nodes.length;
  if (block.kind === "map") {
    const g = geoOf(data, block.hints);
    return g.points.length + g.shapes.length;
  }
  if (block.kind === "record" || block.kind === "kv") return fieldsOf(recordOf(data, block.hints), block.hints).length;
  return rowsOf(data, block.hints).length;
}
